import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { 
  FolderPlus, 
  FileText, 
  ListPlus,
  CheckSquare,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { projectsApi, protocolsApi, usersApi, Project, Protocol } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';

type CreateMode = 'menu' | 'project' | 'protocol' | 'task';

export function CreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { hapticFeedback, showAlert } = useTelegram();
  
  const [mode, setMode] = useState<CreateMode>('menu');
  const [step, setStep] = useState(1);

  // Гарантируем, что при открытии страницы /create всегда показывается меню
  useEffect(() => {
    if (location.pathname === '/create') {
      setMode('menu');
      setStep(1);
      setSelectedProject(null);
      setProjectName('');
      setProtocolData({ title: '', meetingDate: format(new Date(), 'yyyy-MM-dd'), theme: '' });
    }
  }, [location.pathname]);
  
  // Данные для создания
  const [projectName, setProjectName] = useState('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [, setSelectedProtocol] = useState<Protocol | null>(null);
  const [protocolData, setProtocolData] = useState({
    title: '',
    meetingDate: format(new Date(), 'yyyy-MM-dd'),
    theme: '',
  });

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsApi.list,
  });

  // Принудительно обновляем данные пользователя при загрузке страницы
  const { data: currentUser, isLoading: userLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => usersApi.me(),
    staleTime: 0, // Всегда считаем данные устаревшими
    gcTime: 0, // Не кешируем
    refetchOnMount: true, // Всегда обновляем при монтировании
    refetchOnWindowFocus: true, // Обновляем при фокусе окна
  });

  // Проверяем isSystemAdmin из разных источников
  const isSystemAdminFromData = Boolean((currentUser as any)?.isSystemAdmin);
  const isSystemAdmin = isSystemAdminFromData;
  
  // Отладочная информация (можно убрать в продакшене)
  useEffect(() => {
    if (currentUser) {
      console.log('[CreatePage] User data:', {
        userId: currentUser.id,
        isSystemAdmin,
        isSystemAdminFromData,
        currentUserData: currentUser,
      });
    }
  }, [currentUser, isSystemAdmin, isSystemAdminFromData]);
  
  // Проверяем, является ли пользователь менеджером
  const hasManagerRole = projects?.some((p: any) => ['ADMIN', 'MANAGER', 'SYSTEM_ADMIN'].includes(p.role)) || false;
  const isManager = isSystemAdmin || hasManagerRole;

  const { data: protocols, isLoading: protocolsLoading } = useQuery({
    queryKey: ['protocols', selectedProject?.id],
    queryFn: () => protocolsApi.list(selectedProject!.id),
    enabled: !!selectedProject,
  });

  const createProjectMutation = useMutation({
    mutationFn: projectsApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      hapticFeedback('success');
      showAlert('Проект создан!');
      navigate(`/projects/${data.id}/protocols`);
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const createProtocolMutation = useMutation({
    mutationFn: protocolsApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['protocols', selectedProject?.id] });
      hapticFeedback('success');
      showAlert('Протокол создан!');
      navigate(`/protocols/${data.id}`);
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const handleCreateProject = (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectName.trim()) return;
    createProjectMutation.mutate({ name: projectName.trim() });
  };

  const handleCreateProtocol = (e: React.FormEvent) => {
    e.preventDefault();
    if (!protocolData.title.trim() || !selectedProject) return;
    
    createProtocolMutation.mutate({
      projectId: selectedProject.id,
      title: protocolData.title.trim(),
      meetingDate: protocolData.meetingDate,
      theme: protocolData.theme.trim() || undefined,
    });
  };

  const handleSelectProjectForTask = (project: Project) => {
    hapticFeedback('light');
    setSelectedProject(project);
    setStep(2);
  };

  const handleSelectProtocolForTask = (protocol: Protocol) => {
    hapticFeedback('light');
    navigate(`/protocols/${protocol.id}/tasks/new`);
  };

  const resetToMenu = () => {
    setMode('menu');
    setStep(1);
    setSelectedProject(null);
    setSelectedProtocol(null);
    setProjectName('');
    setProtocolData({ title: '', meetingDate: format(new Date(), 'yyyy-MM-dd'), theme: '' });
  };

  // Главное меню создания
  if (mode === 'menu') {
    // Показываем загрузку, если данные пользователя еще загружаются
    if (userLoading) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner />
        </div>
      );
    }
    
    return (
      <div className="p-4 space-y-4">
        <h1 className="text-2xl font-bold text-tg-text mb-6">Создать</h1>
        
        {isSystemAdmin && (
          <Card
            variant="interactive"
            onClick={() => { hapticFeedback('light'); setMode('project'); }}
            className="p-5"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                <FolderPlus className="w-6 h-6 text-blue-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-tg-text">Новый проект</h3>
                <p className="text-sm text-tg-hint">Создайте проект для организации протоколов</p>
              </div>
              <ChevronRight className="w-5 h-5 text-tg-hint" />
            </div>
          </Card>
        )}
        
        <Card
          variant="interactive"
          onClick={() => { hapticFeedback('light'); setMode('protocol'); setStep(1); }}
          className="p-5"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-green-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-tg-text">Новый протокол</h3>
              <p className="text-sm text-tg-hint">Добавьте протокол совещания в проект</p>
            </div>
            <ChevronRight className="w-5 h-5 text-tg-hint" />
          </div>
        </Card>
        
        <Card
          variant="interactive"
          onClick={() => { hapticFeedback('light'); setMode('task'); setStep(1); }}
          className="p-5"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-orange-500/10 flex items-center justify-center">
              <ListPlus className="w-6 h-6 text-orange-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-tg-text">Новая задача</h3>
              <p className="text-sm text-tg-hint">Быстро добавьте задачу в протокол</p>
            </div>
            <ChevronRight className="w-5 h-5 text-tg-hint" />
          </div>
        </Card>
        
        <Card
          variant="interactive"
          onClick={() => { 
            hapticFeedback('light'); 
            navigate('/checklists/new'); 
          }}
          className="p-5"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center">
              <CheckSquare className="w-6 h-6 text-purple-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-tg-text">Новый чек-лист</h3>
              <p className="text-sm text-tg-hint">
                {isManager 
                  ? 'Создайте чек-лист для подчиненных или личный чек-лист'
                  : 'Создайте личный чек-лист с повторяющимися задачами'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-tg-hint" />
          </div>
        </Card>
      </div>
    );
  }

  // Создание проекта
  if (mode === 'project') {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-6">
          <button onClick={resetToMenu} className="text-tg-link">← Назад</button>
        </div>
        
        <h1 className="text-2xl font-bold text-tg-text mb-6">Новый проект</h1>
        
        <form onSubmit={handleCreateProject} className="space-y-4">
          <Input
            label="Название проекта"
            placeholder="Например: Пятницкое шоссе (Дублер)"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            autoFocus
          />
          
          <Button
            type="submit"
            isLoading={createProjectMutation.isPending}
            disabled={!projectName.trim()}
            className="w-full"
            size="lg"
          >
            Создать проект
          </Button>
        </form>
      </div>
    );
  }

  // Создание протокола
  if (mode === 'protocol') {
    // Шаг 1: Выбор проекта
    if (step === 1) {
      return (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6">
            <button onClick={resetToMenu} className="text-tg-link">← Назад</button>
          </div>
          
          <h1 className="text-2xl font-bold text-tg-text">Новый протокол</h1>
          <p className="text-tg-hint mt-1 mb-6">Шаг 1: Выберите проект</p>
          
          {projectsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : projects && projects.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-tg-hint mb-4">У вас ещё нет проектов</p>
              <Button onClick={() => setMode('project')}>
                Создать проект
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {projects?.map((project) => (
                <Card
                  key={project.id}
                  variant="interactive"
                  onClick={() => {
                    hapticFeedback('light');
                    setSelectedProject(project);
                    setStep(2);
                  }}
                  className="p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-tg-text">{project.name}</h3>
                      <p className="text-sm text-tg-hint">
                        {project.protocolsCount} протоколов
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-tg-hint" />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Шаг 2: Форма создания протокола
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-6">
          <button onClick={() => setStep(1)} className="text-tg-link">← Назад</button>
        </div>
        
        <h1 className="text-2xl font-bold text-tg-text">Новый протокол</h1>
        <p className="text-tg-hint mt-1 mb-6">
          Проект: <span className="text-tg-text">{selectedProject?.name}</span>
        </p>
        
        <form onSubmit={handleCreateProtocol} className="space-y-4">
          <Input
            label="Название протокола"
            placeholder="Протокол совещания №1"
            value={protocolData.title}
            onChange={(e) => setProtocolData({ ...protocolData, title: e.target.value })}
            autoFocus
          />
          
          <Input
            type="date"
            label="Дата совещания"
            value={protocolData.meetingDate}
            onChange={(e) => setProtocolData({ ...protocolData, meetingDate: e.target.value })}
          />
          
          <Input
            label="Тема (опционально)"
            placeholder="Общая организация работ"
            value={protocolData.theme}
            onChange={(e) => setProtocolData({ ...protocolData, theme: e.target.value })}
          />
          
          <Button
            type="submit"
            isLoading={createProtocolMutation.isPending}
            disabled={!protocolData.title.trim()}
            className="w-full"
            size="lg"
          >
            Создать протокол
          </Button>
        </form>
      </div>
    );
  }

  // Создание задачи (выбор проекта -> протокола -> переход на форму)
  if (mode === 'task') {
    // Шаг 1: Выбор проекта
    if (step === 1) {
      return (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6">
            <button onClick={resetToMenu} className="text-tg-link">← Назад</button>
          </div>
          
          <h1 className="text-2xl font-bold text-tg-text">Новая задача</h1>
          <p className="text-tg-hint mt-1 mb-6">Шаг 1: Выберите проект</p>
          
          {projectsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : projects && projects.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-tg-hint mb-4">У вас ещё нет проектов</p>
              <Button onClick={() => setMode('project')}>
                Создать проект
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {projects?.map((project) => (
                <Card
                  key={project.id}
                  variant="interactive"
                  onClick={() => handleSelectProjectForTask(project)}
                  className="p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-tg-text">{project.name}</h3>
                      <p className="text-sm text-tg-hint">
                        {project.protocolsCount} протоколов
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-tg-hint" />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Шаг 2: Выбор протокола
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-6">
          <button onClick={() => setStep(1)} className="text-tg-link">← Назад</button>
        </div>
        
        <h1 className="text-2xl font-bold text-tg-text">Новая задача</h1>
        <p className="text-tg-hint mt-1 mb-6">
          Шаг 2: Выберите протокол в проекте <span className="text-tg-text">{selectedProject?.name}</span>
        </p>
        
        {protocolsLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : protocols && protocols.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-tg-hint mb-4">В этом проекте ещё нет протоколов</p>
            <Button onClick={() => { setMode('protocol'); setStep(2); }}>
              Создать протокол
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {protocols?.map((protocol) => (
              <Card
                key={protocol.id}
                variant="interactive"
                onClick={() => handleSelectProtocolForTask(protocol)}
                className="p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-tg-text">{protocol.title}</h3>
                    <p className="text-sm text-tg-hint">
                      {protocol.tasksCount} задач
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-tg-hint" />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}

